'use client';
import { Sparkles, PieChart, FolderLock, MessageCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';

export function Features() {
  const t = useTranslations('landing.features');

  const features = [
    {
      id: 'smart-match',
      icon: Sparkles,
      badge: t('smartMatchBadge'),
      title: t('smartMatchTitle'),
      subtitle: t('smartMatchSubtitle'),
      description: t('smartMatchDesc'),
      highlights: [
        t('smartMatchHighlight1'),
        t('smartMatchHighlight2'),
        t('smartMatchHighlight3'),
        t('smartMatchHighlight4'),
      ],
      visual: (
        <div className="relative">
          {/* Chat-like interface */}
          <div className="bg-card rounded-2xl border shadow-xl p-6 max-w-md mx-auto">
            {/* User message */}
            <div className="flex justify-end mb-4">
              <div className="bg-primary text-primary-foreground rounded-2xl rounded-br-md px-4 py-3 max-w-[80%]">
                <p className="text-sm">
                  I need catering for a company toast, 200 people, October 15th,
                  budget around 50k
                </p>
              </div>
            </div>

            {/* AI response */}
            <div className="flex justify-start">
              <div className="bg-accent rounded-2xl rounded-bl-md px-4 py-3 max-w-[90%]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                    <Sparkles className="w-3 h-3 text-primary" />
                  </div>
                  <span className="text-sm font-medium">Tarboot AI</span>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Found 3 perfect matches for your event:
                </p>

                {/* Vendor cards */}
                <div className="space-y-2">
                  {[
                    {
                      name: 'Gourmet Events',
                      match: 98,
                      price: '45k',
                      avail: true,
                    },
                    {
                      name: 'Elite Catering',
                      match: 95,
                      price: '48k',
                      avail: true,
                    },
                    {
                      name: 'Taste Masters',
                      match: 92,
                      price: '52k',
                      avail: true,
                    },
                  ].map((vendor, i) => (
                    <div
                      key={vendor.name}
                      className="flex items-center justify-between p-3 bg-background rounded-xl border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center font-bold text-amber-700">
                          {i + 1}
                        </div>
                        <div>
                          <div className="font-medium text-sm">
                            {vendor.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Est. {vendor.price} ILS
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-emerald-600">
                          {vendor.match}%
                        </div>
                        <div className="text-xs text-muted-foreground">
                          match
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Floating elements */}
          <div className="absolute -top-4 -left-4 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium animate-float shadow-lg">
            {t('smartMatchFloating1')}
          </div>
          <div className="absolute -bottom-4 -right-4 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-medium animate-float-delayed shadow-lg">
            {t('smartMatchFloating2')}
          </div>
        </div>
      ),
      reversed: false,
    },
    {
      id: 'budget-tracker',
      icon: PieChart,
      badge: t('budgetBadge'),
      title: t('budgetTitle'),
      subtitle: t('budgetSubtitle'),
      description: t('budgetDesc'),
      highlights: [
        t('budgetHighlight1'),
        t('budgetHighlight2'),
        t('budgetHighlight3'),
        t('budgetHighlight4'),
      ],
      visual: (
        <div className="relative">
          <div className="bg-card rounded-2xl border shadow-xl p-6 max-w-md mx-auto">
            {/* Budget header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-sm text-muted-foreground">
                  Total Budget
                </div>
                <div className="text-2xl font-display font-bold">
                  150,000 ILS
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Remaining</div>
                <div className="text-2xl font-display font-bold text-emerald-600">
                  45,200 ILS
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="h-4 bg-muted rounded-full overflow-hidden mb-6">
              <div
                className="h-full bg-gradient-to-r from-primary via-primary to-amber-500 rounded-full transition-all duration-1000"
                style={{ width: '70%' }}
              />
            </div>

            {/* Breakdown */}
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  label: 'Venue',
                  amount: '45,000',
                  pct: 30,
                  color: 'bg-primary',
                },
                {
                  label: 'Catering',
                  amount: '38,000',
                  pct: 25,
                  color: 'bg-amber-500',
                },
                {
                  label: 'Production',
                  amount: '15,800',
                  pct: 11,
                  color: 'bg-violet-500',
                },
                {
                  label: 'Other',
                  amount: '6,000',
                  pct: 4,
                  color: 'bg-sky-500',
                },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${item.color}`} />
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground">
                      {item.label}
                    </div>
                    <div className="text-sm font-medium">{item.amount}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Alert */}
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <span className="text-amber-600 text-lg">!</span>
              </div>
              <div className="text-sm">
                <div className="font-medium text-amber-800">Budget Alert</div>
                <div className="text-amber-600">
                  Catering approaching 90% of allocation
                </div>
              </div>
            </div>
          </div>

          {/* Floating stat */}
          <div className="absolute -top-4 -right-4 bg-emerald-500 text-white px-4 py-2 rounded-xl shadow-lg animate-float">
            <div className="text-2xl font-display font-bold">70%</div>
            <div className="text-xs opacity-90">utilized</div>
          </div>
        </div>
      ),
      reversed: true,
    },
    {
      id: 'document-vault',
      icon: FolderLock,
      badge: t('docsBadge'),
      title: t('docsTitle'),
      subtitle: t('docsSubtitle'),
      description: t('docsDesc'),
      highlights: [
        t('docsHighlight1'),
        t('docsHighlight2'),
        t('docsHighlight3'),
        t('docsHighlight4'),
      ],
      visual: (
        <div className="relative">
          <div className="bg-card rounded-2xl border shadow-xl p-6 max-w-md mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h4 className="font-semibold">Document Vault</h4>
              <span className="text-xs text-muted-foreground">
                12 documents
              </span>
            </div>

            {/* Document list */}
            <div className="space-y-3">
              {[
                {
                  name: 'Venue Contract.pdf',
                  status: 'Signed',
                  statusColor: 'emerald',
                  icon: '📄',
                },
                {
                  name: 'Catering Agreement.pdf',
                  status: 'Pending',
                  statusColor: 'amber',
                  icon: '📄',
                },
                {
                  name: 'Insurance Certificate.pdf',
                  status: 'Valid',
                  statusColor: 'emerald',
                  icon: '🛡️',
                },
                {
                  name: 'AV Quote.pdf',
                  status: 'Approved',
                  statusColor: 'emerald',
                  icon: '📋',
                },
                {
                  name: 'Safety License.pdf',
                  status: 'Expiring Soon',
                  statusColor: 'red',
                  icon: '⚠️',
                },
              ].map((doc) => (
                <div
                  key={doc.name}
                  className="flex items-center justify-between p-3 bg-accent/50 rounded-xl hover:bg-accent transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{doc.icon}</span>
                    <span className="font-medium text-sm">{doc.name}</span>
                  </div>
                  <span
                    className={`text-xs font-medium px-2 py-1 rounded-full ${
                      doc.statusColor === 'emerald'
                        ? 'bg-emerald-100 text-emerald-700'
                        : doc.statusColor === 'amber'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {doc.status}
                  </span>
                </div>
              ))}
            </div>

            {/* Upload zone */}
            <div className="mt-4 border-2 border-dashed border-muted rounded-xl p-4 text-center">
              <div className="text-muted-foreground text-sm">
                Drop files here or{' '}
                <span className="text-primary cursor-pointer">browse</span>
              </div>
            </div>
          </div>

          {/* Compliance badge */}
          <div className="absolute -bottom-4 -left-4 bg-emerald-500 text-white px-4 py-2 rounded-xl shadow-lg animate-float-delayed">
            <div className="flex items-center gap-2">
              <FolderLock className="w-4 h-4" />
              <span className="text-sm font-medium">Compliance Ready</span>
            </div>
          </div>
        </div>
      ),
      reversed: false,
    },
    {
      id: 'whatsapp-bot',
      icon: MessageCircle,
      badge: t('whatsappBadge'),
      title: t('whatsappTitle'),
      subtitle: t('whatsappSubtitle'),
      description: t('whatsappDesc'),
      highlights: [
        t('whatsappHighlight1'),
        t('whatsappHighlight2'),
        t('whatsappHighlight3'),
        t('whatsappHighlight4'),
      ],
      visual: (
        <div className="relative">
          {/* Phone mockup */}
          <div className="bg-foreground rounded-[2.5rem] p-3 max-w-[280px] mx-auto shadow-2xl">
            <div className="bg-gradient-to-b from-emerald-600 to-emerald-700 rounded-[2rem] overflow-hidden">
              {/* WhatsApp header */}
              <div className="bg-emerald-700 px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <span className="text-white font-bold">T</span>
                </div>
                <div className="text-white">
                  <div className="font-semibold text-sm">Tarboot Assistant</div>
                  <div className="text-xs opacity-75">online</div>
                </div>
              </div>

              {/* Chat */}
              <div className="bg-[#e5ddd5] p-3 min-h-[280px] space-y-2">
                {/* Incoming */}
                <div className="bg-white rounded-lg rounded-tl-none p-3 max-w-[85%] shadow-sm">
                  <p className="text-sm text-gray-800">
                    Hi Sarah! You have 2 quotes pending approval for the Annual
                    Gala.
                  </p>
                  <div className="text-[10px] text-gray-500 text-right mt-1">
                    10:30 AM
                  </div>
                </div>

                {/* Quick actions */}
                <div className="bg-white rounded-lg rounded-tl-none p-3 max-w-[85%] shadow-sm">
                  <p className="text-sm text-gray-800 mb-2">Quick actions:</p>
                  <div className="flex flex-wrap gap-2">
                    <button className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                      View Quotes
                    </button>
                    <button className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                      Approve All
                    </button>
                  </div>
                </div>

                {/* Outgoing */}
                <div className="bg-[#dcf8c6] rounded-lg rounded-tr-none p-3 max-w-[85%] ms-auto shadow-sm">
                  <p className="text-sm text-gray-800">Approve all</p>
                  <div className="text-[10px] text-gray-500 text-right mt-1">
                    10:32 AM ✓✓
                  </div>
                </div>

                {/* Confirmation */}
                <div className="bg-white rounded-lg rounded-tl-none p-3 max-w-[85%] shadow-sm">
                  <p className="text-sm text-gray-800">
                    Done! Both vendors have been notified. Your event budget is
                    now 78% allocated.
                  </p>
                  <div className="text-[10px] text-gray-500 text-right mt-1">
                    10:32 AM
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Floating notification */}
          <div className="absolute -top-4 -right-4 bg-white px-4 py-2 rounded-xl shadow-lg animate-float border">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                <MessageCircle className="w-3 h-3 text-white" />
              </div>
              <span className="text-sm font-medium">Instant Updates</span>
            </div>
          </div>
        </div>
      ),
      reversed: true,
    },
  ];

  return (
    <section className="py-20 md:py-28">
      <div className="container-landing">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent text-sm font-medium mb-6 border border-primary/20">
            {t('sectionBadge')}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display tracking-tight">
            {t('heading')}{' '}
            <span className="text-gradient">{t('headingHighlight')}</span>
          </h2>
        </div>

        {/* Feature sections */}
        <div className="space-y-32 md:space-y-40">
          {features.map((feature) => (
            <div
              key={feature.id}
              className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                feature.reversed ? 'lg:flex-row-reverse' : ''
              }`}
            >
              {/* Content */}
              <div className={feature.reversed ? 'lg:order-2' : ''}>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                  <feature.icon className="w-4 h-4" />
                  {feature.badge}
                </div>
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-display tracking-tight mb-3">
                  {feature.title}
                </h3>
                <p className="text-lg text-primary font-medium mb-4">
                  {feature.subtitle}
                </p>
                <p className="text-muted-foreground text-base md:text-lg mb-8 leading-relaxed">
                  {feature.description}
                </p>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {feature.highlights.map((highlight) => (
                    <li
                      key={highlight}
                      className="flex items-center gap-2 text-sm font-medium"
                    >
                      <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-emerald-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Visual */}
              <div className={feature.reversed ? 'lg:order-1' : ''}>
                {feature.visual}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
